// Basic strategy — multi-deck, S17, DAS, late surrender.
// Codes: H hit · S stand · D double-else-hit · Ds double-else-stand
//        R surrender-else-hit · P split · Ph split-if-DAS-else-hit
//        Rh surrender-else-hit (16 only — already R)
// Source: Wizard of Odds reference chart.

import { handTotal, isPair, pairKey } from './engine.js';

function upIndex(rank) {
  if (rank === 'A') return 9;
  if (['10', 'J', 'Q', 'K'].includes(rank)) return 8;
  return parseInt(rank, 10) - 2;
}

const HARD = {
  5:  ['H','H','H','H','H','H','H','H','H','H'],
  6:  ['H','H','H','H','H','H','H','H','H','H'],
  7:  ['H','H','H','H','H','H','H','H','H','H'],
  8:  ['H','H','H','H','H','H','H','H','H','H'],
  9:  ['H','D','D','D','D','H','H','H','H','H'],
  10: ['D','D','D','D','D','D','D','D','H','H'],
  11: ['D','D','D','D','D','D','D','D','D','D'],
  12: ['H','H','S','S','S','H','H','H','H','H'],
  13: ['S','S','S','S','S','H','H','H','H','H'],
  14: ['S','S','S','S','S','H','H','H','H','H'],
  15: ['S','S','S','S','S','H','H','H','H','R'],
  16: ['S','S','S','S','S','H','H','H','R','R'],
  17: ['S','S','S','S','S','S','S','S','S','S'],
  18: ['S','S','S','S','S','S','S','S','S','S'],
  19: ['S','S','S','S','S','S','S','S','S','S'],
  20: ['S','S','S','S','S','S','S','S','S','S'],
  21: ['S','S','S','S','S','S','S','S','S','S'],
};

const SOFT = {
  13: ['H','H','H','D','D','H','H','H','H','H'],   // A,2
  14: ['H','H','H','D','D','H','H','H','H','H'],   // A,3
  15: ['H','H','D','D','D','H','H','H','H','H'],   // A,4
  16: ['H','H','D','D','D','H','H','H','H','H'],   // A,5
  17: ['H','D','D','D','D','H','H','H','H','H'],   // A,6
  18: ['Ds','Ds','Ds','Ds','Ds','S','S','H','H','H'], // A,7
  19: ['S','S','S','S','S','S','S','S','S','S'],
  20: ['S','S','S','S','S','S','S','S','S','S'],
};

// Pair splits — DAS rules. P = always split, Ph = split only if DAS.
const PAIRS = {
  '2':  ['Ph','Ph','P','P','P','P','H','H','H','H'],
  '3':  ['Ph','Ph','P','P','P','P','H','H','H','H'],
  '4':  ['H','H','H','Ph','Ph','H','H','H','H','H'],
  '5':  ['D','D','D','D','D','D','D','D','H','H'],   // never split 5s
  '6':  ['Ph','P','P','P','P','H','H','H','H','H'],
  '7':  ['P','P','P','P','P','P','H','H','H','H'],
  '8':  ['P','P','P','P','P','P','P','P','P','P'],   // always split 8s
  '9':  ['P','P','P','P','P','S','P','P','S','S'],
  '10': ['S','S','S','S','S','S','S','S','S','S'],   // never split 10s
  'A':  ['P','P','P','P','P','P','P','P','P','P'],   // always split aces
};

export function basicStrategyAction(playerCards, dealerUpRank, opts = {}) {
  const { canDouble = true, canSurrender = false, canSplit = false, das = true } = opts;
  const ui = upIndex(dealerUpRank);
  let code;
  if (canSplit && isPair(playerCards)) {
    code = PAIRS[pairKey(playerCards[0].rank)][ui];
  } else {
    const { total, soft } = handTotal(playerCards);
    if (soft && total >= 13 && total <= 20) code = SOFT[total][ui];
    else code = HARD[Math.max(5, Math.min(21, total))][ui];
  }
  return resolveCode(code, { canDouble, canSurrender, das });
}

function resolveCode(code, { canDouble, canSurrender, das }) {
  switch (code) {
    case 'H': return 'hit';
    case 'S': return 'stand';
    case 'D': return canDouble ? 'double' : 'hit';
    case 'Ds': return canDouble ? 'double' : 'stand';
    case 'R': return canSurrender ? 'surrender' : 'hit';
    case 'P': return 'split';
    case 'Ph': return das ? 'split' : 'hit';
    default: return 'stand';
  }
}

export function reasonFor(playerCards, dealerUpRank, action) {
  const up = dealerUpRank === '10' ? 'T' : dealerUpRank;
  if (isPair(playerCards) && (action === 'split' || action === 'hit' || action === 'stand')) {
    const r = pairKey(playerCards[0].rank);
    if (action === 'split') {
      if (r === '8') return `Pair of 8s vs ${up}: always split. 16 is the worst hand in blackjack — two hands of 8 are far better.`;
      if (r === 'A') return `Pair of aces vs ${up}: always split. One ace + one card is much stronger than 12 — only one card per ace though.`;
      return `Pair of ${r}s vs ${up}: split. Two hands starting with ${r} have higher combined EV than one bad total.`;
    }
    if (r === '10' && action === 'stand') return `Pair of 10s vs ${up}: never split. 20 is the second-best hand in the game — keep it.`;
    if (r === '5') return `Pair of 5s vs ${up}: treat as a 10, ${action}. Splitting 5s gives you two weak hands instead of a strong starting total.`;
  }
  const { total, soft } = handTotal(playerCards);
  const handDesc = soft ? `soft ${total}` : `${total}`;
  const messages = {
    hit: `${handDesc} vs ${up}: hit. Dealer is strong (or your total is weak enough that improving beats standing).`,
    stand: `${handDesc} vs ${up}: stand. Dealer is more likely to bust than you are to safely improve.`,
    double: `${handDesc} vs ${up}: double. Dealer is weak or your total is strong — get the extra unit in.`,
    surrender: `${handDesc} vs ${up}: surrender. Expected loss exceeds 50% — give up half rather than play it out.`,
    split: `${handDesc} vs ${up}: split.`,
  };
  return messages[action] || `${handDesc} vs ${up}: ${action}.`;
}

// Insurance: take only at true count >= 3 (Illustrious 18 index).
export function insuranceRecommendation(trueCount) {
  if (trueCount >= 3) return { take: true, reason: `True count ${trueCount.toFixed(1)} ≥ +3. The remaining shoe is rich in tens — insurance is now +EV.` };
  return { take: false, reason: `Insurance is a sucker bet without a count. Only +EV when true count ≥ +3 (this is the most famous count deviation, Illustrious 18 #1).` };
}
